import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

export class LocationCards {
  constructor(listSelector = '.where__locations', { pageSize = 6 } = {}) {
    this.list =
      typeof listSelector === 'string'
        ? document.querySelector(listSelector)
        : listSelector;

    this.pageSize = pageSize;

    // State
    this.data = [];
    this.index = 0;
    this.sentinel = null;

    // GSAP/ScrollTrigger refs
    this.listST = null;
    this.sentinelST = null;
    this.cardTweens = [];
  }

  showCards(data = []) {
    // Full reset on each call
    this.cleanup();

    if (!this.list || !Array.isArray(data) || data.length === 0) return;

    this.data = data;
    this.index = 0;

    // Sentinel at the end of the list
    this.sentinel = document.createElement('li');
    this.sentinel.className = 'where__sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.sentinel.style.listStyle = 'none';
    this.sentinel.style.margin = '0';
    this.sentinel.style.padding = '0';
    this.sentinel.style.height = '1px';
    this.sentinel.style.opacity = '0';
    this.list.appendChild(this.sentinel);

    // Load first chunk when the list enters viewport
    this.listST = ScrollTrigger.create({
      trigger: this.list,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        this.appendNextChunk();
        ScrollTrigger.refresh();
      },
    });

    // Load subsequent chunks when sentinel appears
    this.sentinelST = ScrollTrigger.create({
      trigger: this.sentinel,
      start: 'top 95%',
      onEnter: () => {
        const hasMore = this.appendNextChunk();
        ScrollTrigger.refresh();
        if (!hasMore) {
          this.sentinelST?.kill();
          this.sentinel?.remove();
          this.sentinelST = null;
          this.sentinel = null;
        }
      },
    });
  }

  appendNextChunk() {
    if (this.index >= this.data.length) return false;

    const end = Math.min(this.index + this.pageSize, this.data.length);
    for (let i = this.index; i < end; i++) {
      const html = this.createCardHTML(this.data[i]);
      this.sentinel.insertAdjacentHTML('beforebegin', html);

      const card = this.sentinel.previousElementSibling;
      const tween = gsap.from(card, {
        autoAlpha: 0,
        y: 24,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: card,
          start: 'top 85%',
          once: true,
        },
      });
      if (tween) this.cardTweens.push(tween);
    }

    this.index = end;
    return this.index < this.data.length;
  }

  createCardHTML(item) {
    const title = this.text(item?.title || item?.name || '');

    // Prefer array fields; if missing, fall back to the first matching contact
    const address = this.text(
      (Array.isArray(item?.addresses) && item.addresses[0]) ||
        this.firstContactValue(item, ['address']) ||
        '',
    );

    const phoneRaw =
      (Array.isArray(item?.phones) && item.phones[0]) ||
      this.firstContactValue(item, ['phone', 'tel']) ||
      '';

    const siteRaw =
      (Array.isArray(item?.websites) && item.websites[0]) ||
      this.firstContactValue(item, ['www', 'website', 'url', 'site', 'link']) ||
      '';

    const igRaw =
      (Array.isArray(item?.instagrams) && item.instagrams[0]) ||
      this.firstContactValue(item, ['instagram', 'ig']) ||
      '';

    const phoneHref = phoneRaw ? this.toTelHref(phoneRaw) : '';
    const siteDomain = siteRaw ? this.domainFromUrl(siteRaw) : '';
    const igHandle = igRaw ? this.normalizeInstagram(igRaw) : '';

    const phoneBlock = phoneHref
      ? `<a href="${phoneHref}" class="location-card__contact" aria-label="Call ${this.attr(title)}">${this.text(phoneRaw)}</a>`
      : '';

    const urlsInner = [
      siteRaw
        ? `<a href="${this.attr(siteRaw)}" class="location-card__url" target="_blank" rel="nofollow noreferrer noopener"><span>${this.text(siteDomain)}</span></a>`
        : '',
      igHandle
        ? `<a href="https://www.instagram.com/${this.attr(igHandle)}/" class="location-card__url" target="_blank" rel="nofollow noreferrer noopener"><span>@${this.text(igHandle)}</span></a>`
        : '',
    ].join('');

    const urlsBlock = urlsInner
      ? `<div class="location-card__urls">${urlsInner}</div>`
      : '';

    // Keep the resulting markup exactly as specified
    return `
      <li class="location-card">
        <p class="location-card__title">${title}</p>
        <div class="location-card__contacts">
          <p class="location-card__contact">${address}</p>
          ${phoneBlock}
        </div>
        ${urlsBlock}
      </li>
    `.trim();
  }

  // Helpers
  firstContactValue(item, types = []) {
    const contacts = Array.isArray(item?.contacts) ? item.contacts : [];
    const set = new Set(types.map((t) => String(t).toLowerCase().trim()));
    for (const c of contacts) {
      const t = String(c?.type || '')
        .toLowerCase()
        .trim();
      if (set.has(t)) return c?.value || '';
    }
    return '';
  }

  normalizeInstagram(handle) {
    return String(handle || '')
      .trim()
      .replace(/^@/, '')
      .replace(/\/+$/, '');
  }

  toTelHref(phone) {
    const s = String(phone || '');
    const hasPlus = s.trim().startsWith('+');
    const digits = s.replace(/[^\d]/g, '');
    return hasPlus ? `tel:+${digits}` : `tel:${digits}`;
  }

  domainFromUrl(url) {
    try {
      const u = new URL(url);
      return u.hostname.replace(/^www\./i, '');
    } catch {
      return String(url || '');
    }
  }

  text(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  attr(s) {
    return this.text(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  cleanup() {
    // Kill triggers
    this.listST?.kill();
    this.sentinelST?.kill();
    this.listST = null;
    this.sentinelST = null;

    // Kill per-card tweens (and their ScrollTriggers)
    this.cardTweens.forEach((t) => {
      try {
        t.scrollTrigger?.kill();
        t.kill();
      } catch {}
    });
    this.cardTweens = [];

    // Remove sentinel
    if (this.sentinel && this.sentinel.parentNode === this.list) {
      this.sentinel.remove();
    }
    this.sentinel = null;

    // Remove previously inserted cards
    if (this.list) {
      this.list.querySelectorAll('.location-card').forEach((el) => el.remove());
    }

    // Reset data/index
    this.data = [];
    this.index = 0;

    // Refresh after DOM changes
    if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.refresh) {
      ScrollTrigger.refresh();
    }
  }
}
